/**
 * @module errors
 * @package @geenius/adapters/neon
 * @description Defines Neon-specific binding errors for adapter construction
 * and migration helper validation.
 */

import { AdapterConfigurationError } from "@geenius/adapters";
import type { NeonBindingErrorCode } from "./types";

/**
 * Error thrown when Neon adapter binding, factory, or migration inputs do not
 * satisfy the SDK-free contract expected by this package.
 */
export class NeonBindingError extends AdapterConfigurationError {
  readonly neonCode: NeonBindingErrorCode;

  constructor(
    message: string,
    code: NeonBindingErrorCode = "INVALID_NEON_ADAPTER",
    cause?: unknown,
  ) {
    super(message, "INVALID_CONFIG", cause);
    this.neonCode = code;
    Object.defineProperty(this, "name", {
      value: "NeonBindingError",
      enumerable: false,
      configurable: true,
    });
    Object.defineProperty(this, "code", {
      value: code,
      enumerable: true,
      configurable: true,
    });
    Object.defineProperty(this, "adapterCode", {
      value: code,
      enumerable: true,
      configurable: true,
    });
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * Checks whether an unknown failure came from Neon binding or migration
 * validation rather than a downstream database client.
 */
export function isNeonBindingError(error: unknown): error is NeonBindingError {
  return error instanceof NeonBindingError;
}
